import apiSvc, { BinaryFileType } from '../../../core/services/apiSvc';
import utilSvc from '../../../core/utilSvc';
import { integrityDesc } from './systemDescriptions';
// import mockMonitors from './mockMonitors.json';

export type MonitorItem = {
    Id: string;
    Status: string;
    CE: string;
    RXVEN: string;
    MonitorType: string;
    Changed: string;
    LastChecked: string;
    SubscribedEmail: string;
    SubscribedInApp: string;
    AlertEmailPrompt: string;
    AlertInAppPrompt: string;
};

export type MonitorHistoryItem = {
    Status: string;
    ElapsedTxt: string;
    Changed: string;
    LastChecked: string;
    Details: string;
};

export type MonitorConfig = {
    Active: boolean;
    Alert: boolean;
    Interval: number;
    IntervalType: string;
    Lookback?: number;
    LookbackType?: string;
};

// Monitor Status VERITY variant (ENT-2732x): query-driven datafeed search + config edit.
export type DatafeedQuery = {
    CE: string[];
    RX: string[];
    MonitorType: string[];
    Status: string[];
    SubscribedEmail: boolean;
    SubscribedInApp: boolean;
};

// Full config detail for the VERITYSA edit flyout (ActiveDefault gates whether the monitor may be turned on).
export type MonitorConfigDetail = {
    Id: string;
    ConfigType: string;
    ConfigName: string;
    Active: boolean;
    Alert: boolean;
    ActiveDefault: boolean;
    IntervalVal: number;
    IntervalType: string;
    LookbackVal: number;
    LookbackType: string;
};

// System tab (VERITY): a single System/Integrity health row plus its lazily-loaded history events.
export type SystemStatEvent = {
    Status: string;
    ElapsedTxt: string;
    Changed: Date | null;
    LastChecked: Date | null;
    Variance: number | null;
    Details: string;
};

export type SystemStatItem = {
    Id: string;
    MonitorType: string;
    Status: string;
    name: string;
    desc: string;
    Variance: number | null;
    Changed: Date | null;
    LastChecked: Date | null;
    StartTime: Date | null;
    ElapsedTxt: string;
    hasDetail: boolean;
    expand: boolean;
    events: SystemStatEvent[];
    eventsLoaded: boolean;
};

export type SystemHealth = {
    system: SystemStatItem[];
    integrity: SystemStatItem[];
    wacOrderingStatus: boolean;
};

export type SystemStatType = 'SYSTEM' | 'INTEGRITY';

// WAC ordering circuit breaker (VERITYSA): held-order disposition on restart.
export type HeldOrderAction = 'EXPORT' | 'CANCEL';

// Defaults tab (VERITY): monitor rule config rows; only VERITYSA can edit via row-click flyout.
export type MonitorDefaultItem = {
    Id: string;
    ConfigType: string;
    ConfigName: string;
    Active: string; // 'YES' | 'NO'
    Alert: string; // 'ON' | 'OFF'
    IntervalVal: number;
    IntervalType: string;
    LookbackVal: number;
    LookbackType: string;
    IntervalTxt: string;
    LookbackTxt: string;
    showLookback: boolean;
};

export type MonitorDefaultSaveModel = {
    Id: string;
    ConfigType: string;
    ConfigName: string;
    Active: boolean;
    Alert: boolean;
    IntervalVal: number;
    IntervalType: string;
    LookbackVal: number;
    LookbackType: string;
};

// Activity tab (VERITY): read-only processing dashboard (ops/monitor/activity/summary).
export type ActivityInboundItem = {
    Type: string;
    TotalReceived: number;
    TotalProcessed: number;
    LastReceived: Date | null;
    LastProcessed: Date | null;
};

export type ActivityCurrentItem = {
    Name: string;
    ProcessingCount: number;
    EstimatedCount: number;
    ProcessStartTime: Date | null;
    ProcessingTime: Date | null;
    LogTime: Date | null;
};

export type ActivityFinishedJobItem = {
    Name: string;
    TotalRuns: number;
    TotalProcessed: number;
    LastRun: Date | null;
    AvgDurationText: string;
    TotalDurationText: string;
};

export type ActivityReEvalItem = {
    Status: string;
    CreateBy: string;
    CreateDate: Date | null;
    StartDate: Date | null;
    ProcessingTime: string;
    Details: string;
};

export type ActivityOtherJobItem = {
    Name: string;
    Status: string;
    UserId: string;
    StartDate: Date | null;
    ProcessingTime: string;
};

export type ActivityLockItem = {
    Key: string;
    HeldBy: string;
    Acquired: Date | null;
};

export type ActivitySummary = {
    inbound: ActivityInboundItem[];
    current: ActivityCurrentItem[];
    finished: ActivityFinishedJobItem[];
    reEval: ActivityReEvalItem[];
    other: ActivityOtherJobItem[];
    locks: ActivityLockItem[];
};

const MONITORS_LIST = 'ops/datamonitor/list';
const MONITORS_HISTORY = 'ops/datamonitor/history';
const MONITORS_CONFIG = 'ops/datamonitor/config';
const MONITORS_SUBSCRIBE = 'ops/datamonitor/alert/subscribe';
const DATAFEED_LIST = 'ops/monitor/datafeed/list';
const DATAFEED_GET = 'ops/monitor/datafeed/get';
const DATAFEED_CONFIG_GET = 'ops/monitor/datafeed/config/get';
const DATAFEED_CONFIG_SAVE = 'ops/monitor/datafeed/config/save';
const BULK_SUBSCRIBE = 'ops/monitor/alert/bulksubscribe';
const PROCESSING_ERRORS_LIST = 'ops/datamonitor/processing/list';
const VERITY_PROCESSING_LIST = 'ops/monitor/processing/list';
const SYSTEM_REFRESH = 'ops/monitor/refresh';
const SYSTEM_STAT_GET = 'ops/monitor/get';
const SYSTEM_REMITTANCE_EXCEL = 'ops/monitor/remittance/excel';
const DEFAULTS_LIST = 'ops/monitor/config/default/list';
const DEFAULTS_SAVE = 'ops/monitor/config/default/save';
const ACTIVITY_SUMMARY = 'ops/monitor/activity/summary';
const WAC_STOP = 'order/wacstop';
const WAC_RESTART = 'order/wacrestart';

// legacy monitor sorts refresh + stat events by Date ascending
const SORT_BY_DATE_ASC = [{ Field: 'Date', Order: 'ASC' }];

const isNullDate = (d: string): boolean => !d || d.startsWith('0001');

const parseVariance = (isIntegrity: boolean, status: string, details: unknown): number | null => {
    if (!isIntegrity || status !== 'FAIL') return null;
    const n = parseInt(String(details), 10);
    return Number.isNaN(n) ? null : n;
};

// legacy: only these two datafeed monitors expose a lookback range
export const defaultsUseLookback = (configName: string): boolean =>
    configName === 'AFFILIATION.ACKNOWLEDGEMENT.RECEIVED' || configName === 'AFFILIATION.INVOICES.RECEIVED';

// legacy datafeed list / bulk subscribe share the same CE/RX context ('ALL' -> empty array = no filter)
const datafeedContext = (q: DatafeedQuery): { Context: { CEID: string[]; RXID: string[] } } => ({
    Context: {
        CEID: q.CE[0] === 'ALL' ? [] : q.CE,
        RXID: q.RX[0] === 'ALL' ? [] : q.RX,
    },
});

// shared row shaping for CE and VERITY monitor lists
function mapMonitorRow(item: any): MonitorItem {
    let rxven = '';
    if (item.PH) rxven += 'PH: ' + item.PH;
    if (item.VEN) {
        if (rxven.length > 0) rxven += ', ';
        rxven += 'VEN: ' + item.VEN.toUpperCase();
    }
    const changed = isNullDate(item.Changed) ? null : new Date(item.Changed);
    const lastChecked = isNullDate(item.LastChecked) ? null : new Date(item.LastChecked);
    return {
        ...item,
        RXVEN: rxven,
        Changed: changed,
        LastChecked: lastChecked,
        SubscribedEmail: item.SubscribedEmail ? 'ON' : 'OFF',
        SubscribedInApp: item.SubscribedInApp ? 'ON' : 'OFF',
        AlertEmailPrompt: item.SubscribedEmail ? 'unsubscribe' : 'subscribe',
        AlertInAppPrompt: item.SubscribedInApp ? 'unsubscribe' : 'subscribe',
    } as MonitorItem;
}

const datamonitorRepo = {
    async getMonitors(ce: string, monitorTypes: string[]): Promise<MonitorItem[]> {
        const body = {
            Context: {
                CEID: ce === 'ALL' ? [] : [ce],
                RXID: [],
            },
            Params: {
                MonitorType: monitorTypes[0] === 'ALL' ? [] : monitorTypes,
                Status: ['PASS', 'FAIL'],
            },
            Paging: { Size: 2000 },
            SortBy: [],
        };
        const r = await apiSvc.post(MONITORS_LIST, body);
        const raw: any[] = r?.Entity ?? [];
        return raw.map(mapMonitorRow);
    },

    // VERITY Monitor Status: query-driven datafeed search (facility-independent, uses ops/monitor/datafeed/list)
    async getVerityMonitors(query: DatafeedQuery): Promise<MonitorItem[]> {
        const body = {
            ...datafeedContext(query),
            Params: {
                MonitorType: query.MonitorType[0] === 'ALL' ? [] : query.MonitorType,
                Status: query.Status,
                SubscribedEmail: query.SubscribedEmail,
                SubscribedInApp: query.SubscribedInApp,
            },
            Paging: { Size: 2000 },
            SortBy: [],
        };
        const r = await apiSvc.post(DATAFEED_LIST, body);
        const raw: any[] = r?.Entity ?? [];
        return raw.map(mapMonitorRow);
    },

    async getHistory(id: string): Promise<MonitorHistoryItem[]> {
        const body = {
            Context: {},
            Params: { Id: id, EventNum: 10 },
            Paging: { Size: 2000 },
            SortBy: [],
        };
        const r = await apiSvc.post(MONITORS_HISTORY, body);
        const raw: any[] = r?.Entity ?? [];
        return raw.map(item => ({
            ...item,
            Changed: isNullDate(item.Changed) ? '' : item.Changed,
            LastChecked: isNullDate(item.LastChecked) ? '' : item.LastChecked,
            ElapsedTxt: utilSvc.dt.secondsFmt(item.Elapsed),
        }));
    },

    // VERITY datafeed event history (uses ops/monitor/datafeed/get, not the CE ops/datamonitor/history)
    async getVerityHistory(id: string): Promise<MonitorHistoryItem[]> {
        const body = {
            Context: {},
            Params: { Id: id, EventNum: 10 },
            Paging: { Size: 2000 },
            SortBy: [],
        };
        const r = await apiSvc.post(DATAFEED_GET, body);
        const raw: any[] = r?.Entity ?? [];
        return raw.map(item => ({
            ...item,
            ElapsedTxt: utilSvc.dt.secondsFmt(item.Elapsed),
        }));
    },

    async getConfig(id: string, isEndUser: boolean): Promise<MonitorConfig> {
        const body = { Params: { Id: id } };
        const endpoint = isEndUser ? MONITORS_CONFIG : DATAFEED_CONFIG_GET;
        const r = await apiSvc.post(endpoint, body);
        const e = r?.Entity;
        if (!e) return { Active: false, Alert: false, Interval: 0, IntervalType: '' };
        return {
            Active: !!e.Active,
            Alert: !!e.Alert,
            Interval: e.IntervalVal ?? 0,
            IntervalType: e.IntervalType ?? '',
            Lookback: e.LookbackVal ?? 0,
            LookbackType: e.LookbackType ?? '',
        };
    },

    // Full config detail for the VERITYSA Monitor Status edit flyout.
    async getDatafeedConfig(id: string): Promise<MonitorConfigDetail> {
        const r = await apiSvc.post(DATAFEED_CONFIG_GET, { Params: { Id: id } });
        const e = r?.Entity ?? {};
        return {
            Id: id,
            ConfigType: e.ConfigType ?? '',
            ConfigName: e.ConfigName ?? '',
            Active: !!e.Active,
            Alert: !!e.Alert,
            ActiveDefault: !!(e.Default && e.Default.Active),
            IntervalVal: e.IntervalVal ?? 0,
            IntervalType: e.IntervalType ?? '',
            LookbackVal: e.LookbackVal ?? 0,
            LookbackType: e.LookbackType ?? '',
        };
    },

    async saveDatafeedConfig(model: MonitorDefaultSaveModel): Promise<void> {
        await apiSvc.post(DATAFEED_CONFIG_SAVE, { Entity: model });
    },

    // VERITY bulk subscribe/unsubscribe for every monitor matching the current query.
    async bulkSubscribe(query: DatafeedQuery, subscribed: boolean): Promise<void> {
        const body = {
            ...datafeedContext(query),
            Params: {
                MonitorType: query.MonitorType[0] === 'ALL' ? [] : query.MonitorType,
                SubscribedEmail: subscribed,
                SubscribedInApp: subscribed,
            },
        };
        await apiSvc.post(BULK_SUBSCRIBE, body);
    },

    async subscribe(id: string, subscribedEmail: boolean, subscribedInApp: boolean): Promise<void> {
        const body = {
            Params: {
                Id: id,
                SubscribedEmail: subscribedEmail,
                SubscribedInApp: subscribedInApp,
            },
        };
        await apiSvc.post(MONITORS_SUBSCRIBE, body);
    },

    async getProcessingErrors(ce: string, isVerity = false): Promise<any[]> {
        // VERITY has no facility scope; the CE endpoint rejects an empty CEID with 400 "CEs Required"
        if (isVerity) {
            const r = await apiSvc.post(VERITY_PROCESSING_LIST, { Context: {} });
            return r?.Entity ?? [];
        }
        const body = {
            Context: {
                CEID: ce === 'ALL' ? [] : [ce],
            },
        };
        const r = await apiSvc.post(PROCESSING_ERRORS_LIST, body);
        return r?.Entity ?? [];
    },

    async getSystemHealth(): Promise<SystemHealth> {
        const today = utilSvc.dt.zeroTime(new Date());
        const body = {
            Context: {},
            Params: {
                StartDate: utilSvc.dt.addDays(today, -7),
                EndDate: today,
            },
            Paging: { Size: 2000 },
            SortBy: SORT_BY_DATE_ASC,
        };
        const r = await apiSvc.post(SYSTEM_REFRESH, body);
        const e = r?.Entity ?? {};
        const system = (Array.isArray(e.System) ? e.System : []).map((x: any) => mapStatItem(x, false));
        const integrity = (Array.isArray(e.Integrity) ? e.Integrity : []).map((x: any) => mapStatItem(x, true));
        return { system, integrity, wacOrderingStatus: !!e.WacOrderingStatus };
    },

    // WAC ordering status shares the System refresh payload; no dedicated status endpoint exists.
    async getWacOrderingStatus(): Promise<boolean> {
        const today = utilSvc.dt.zeroTime(new Date());
        const body = {
            Context: {},
            Params: {
                StartDate: utilSvc.dt.addDays(today, -7),
                EndDate: today,
            },
            Paging: { Size: 2000 },
            SortBy: SORT_BY_DATE_ASC,
        };
        const r = await apiSvc.post(SYSTEM_REFRESH, body);
        return !!r?.Entity?.WacOrderingStatus;
    },

    // system-wide switch to stop all outgoing WAC purchase orders (empty body)
    async stopWacOrders(): Promise<void> {
        await apiSvc.post(WAC_STOP, {});
    },

    // system-wide switch to restart WAC ordering; held orders are exported or cancelled
    async restartWacOrders(heldAction: HeldOrderAction): Promise<void> {
        await apiSvc.post(WAC_RESTART, { Entity: { Params: { HeldOrderAction: heldAction } } });
    },

    async getStatEvents(type: SystemStatType, id: string): Promise<SystemStatEvent[]> {
        // some refresh rows (e.g. an unrecognized/OFF monitor) come back with no Id; the server rejects those
        if (!id) {
            return [];
        }
        const body = {
            Context: {},
            Params: { Id: id, EventNum: 10 },
            Paging: { Size: 2000 },
            SortBy: SORT_BY_DATE_ASC,
        };
        const r = await apiSvc.post(SYSTEM_STAT_GET, body);
        const raw: any[] = Array.isArray(r?.Entity) ? r.Entity : [];
        return raw.map((x: any) => mapStatEvent(x, type === 'INTEGRITY'));
    },

    async getRemittanceExcel(): Promise<void> {
        await apiSvc.postGetBinary(SYSTEM_REMITTANCE_EXCEL, {}, 'discrepancies' + Date.now(), BinaryFileType.Excel);
    },

    async getDefaults(): Promise<MonitorDefaultItem[]> {
        const r = await apiSvc.post(DEFAULTS_LIST, {});
        const raw: any[] = Array.isArray(r?.Entity) ? r.Entity : [];
        return raw.map(mapDefaultItem);
    },

    async saveDefaultsConfig(model: MonitorDefaultSaveModel): Promise<void> {
        await apiSvc.post(DEFAULTS_SAVE, { Entity: model });
    },

    // Activity tab (VERITY): read-only processing dashboard summary.
    async getActivity(): Promise<ActivitySummary> {
        const r = await apiSvc.post(ACTIVITY_SUMMARY, {});
        const e = r?.Entity ?? {};
        return {
            inbound: (Array.isArray(e.InBound) ? e.InBound : []).map(mapActivityInbound),
            current: (Array.isArray(e.CurrentActivites) ? e.CurrentActivites : []).map(mapActivityCurrent),
            finished: (Array.isArray(e.RecentFinishedJobs) ? e.RecentFinishedJobs : []).map(mapActivityFinished),
            reEval: (Array.isArray(e.ReEvalJobs) ? e.ReEvalJobs : []).map(mapActivityReEval),
            other: (Array.isArray(e.OtherUserJobs) ? e.OtherUserJobs : []).map(mapActivityOther),
            locks: (Array.isArray(e.CurrentLocks) ? e.CurrentLocks : []).map(mapActivityLock),
        };
    },
};

function mapStatItem(raw: any, isIntegrity: boolean): SystemStatItem {
    const monitorType = raw.MonitorType || 'UNKNOWN';
    return {
        Id: raw.Id,
        MonitorType: monitorType,
        Status: raw.Status,
        name: monitorType,
        desc: isIntegrity ? integrityDesc(monitorType) : '',
        Variance: parseVariance(isIntegrity, raw.Status, raw.Details),
        Changed: isNullDate(raw.Changed) ? null : new Date(raw.Changed),
        LastChecked: isNullDate(raw.LastChecked) ? null : new Date(raw.LastChecked),
        StartTime: isNullDate(raw.StartTime) ? null : new Date(raw.StartTime),
        ElapsedTxt: utilSvc.dt.secondsFmt(raw.Elapsed),
        hasDetail: monitorType === 'INTEGRITY_INTACCT_LOAD',
        expand: false,
        events: [],
        eventsLoaded: false,
    };
}

function mapStatEvent(raw: any, isIntegrity: boolean): SystemStatEvent {
    return {
        Status: raw.Status,
        ElapsedTxt: utilSvc.dt.secondsFmt(raw.Elapsed),
        Changed: isNullDate(raw.Changed) ? null : new Date(raw.Changed),
        LastChecked: isNullDate(raw.LastChecked) ? null : new Date(raw.LastChecked),
        Variance: parseVariance(isIntegrity, raw.Status, raw.Details),
        Details: raw.Details ?? '',
    };
}

function mapDefaultItem(raw: any): MonitorDefaultItem {
    const configName = raw.ConfigName ?? '';
    const configType = raw.ConfigType ?? '';
    const showLookback = defaultsUseLookback(configName);
    const intervalVal = Number.isFinite(raw.IntervalVal) ? raw.IntervalVal : 0;
    const intervalType = raw.IntervalType ?? '';
    const lookbackVal = Number.isFinite(raw.LookbackVal) ? raw.LookbackVal : 0;
    const lookbackType = raw.LookbackType ?? '';

    // interval/lookback text only applies to editable DATAFEED rows (legacy defaultsVM)
    let intervalTxt = '';
    let lookbackTxt = '';
    if (configType === 'DATAFEED') {
        intervalTxt = `${intervalVal} ${intervalType}S`;
        if (showLookback) {
            lookbackTxt = `${lookbackVal} ${lookbackType}S`;
        }
    }

    return {
        Id: raw.Id ?? '',
        ConfigType: configType,
        ConfigName: configName,
        Active: raw.Active ? 'YES' : 'NO',
        Alert: raw.Alert ? 'ON' : 'OFF',
        IntervalVal: intervalVal,
        IntervalType: intervalType,
        LookbackVal: lookbackVal,
        LookbackType: lookbackType,
        IntervalTxt: intervalTxt,
        LookbackTxt: lookbackTxt,
        showLookback,
    };
}

// legacy okGetActivity uses validSvc.isDate(); treat empty/zero/unparseable values as null
const parseActivityDate = (v: unknown): Date | null => {
    if (!v || (typeof v === 'string' && v.startsWith('0001'))) {
        return null;
    }
    const d = new Date(v as string);
    return Number.isNaN(d.getTime()) ? null : d;
};

function mapActivityInbound(raw: any): ActivityInboundItem {
    return {
        Type: raw.Type ?? '',
        TotalReceived: raw.TotalReceived ?? 0,
        TotalProcessed: raw.TotalProcessed ?? 0,
        LastReceived: parseActivityDate(raw.LastReceived),
        LastProcessed: parseActivityDate(raw.LastProcessed),
    };
}

function mapActivityCurrent(raw: any): ActivityCurrentItem {
    return {
        Name: raw.Name ?? '',
        ProcessingCount: raw.ProcessingCount ?? 0,
        EstimatedCount: raw.EstimatedCount ?? 0,
        ProcessStartTime: parseActivityDate(raw.ProcessStartTime),
        ProcessingTime: parseActivityDate(raw.ProcessingTime),
        LogTime: parseActivityDate(raw.LogTime),
    };
}

function mapActivityFinished(raw: any): ActivityFinishedJobItem {
    return {
        Name: raw.Name ?? '',
        TotalRuns: raw.TotalRuns ?? 0,
        TotalProcessed: raw.TotalProcessed ?? 0,
        LastRun: parseActivityDate(raw.LastRun),
        AvgDurationText: raw.AvgDurationText ?? '',
        TotalDurationText: raw.TotalDurationText ?? '',
    };
}

function mapActivityReEval(raw: any): ActivityReEvalItem {
    return {
        Status: raw.Status ?? '',
        CreateBy: raw.CreateBy ?? '',
        CreateDate: parseActivityDate(raw.CreateDate),
        StartDate: parseActivityDate(raw.StartDate),
        ProcessingTime: raw.ProcessingTime ?? '',
        Details: raw.Details ?? '',
    };
}

function mapActivityOther(raw: any): ActivityOtherJobItem {
    return {
        Name: raw.Name ?? '',
        Status: raw.Status ?? '',
        UserId: raw.UserId ?? '',
        StartDate: parseActivityDate(raw.StartDate),
        ProcessingTime: raw.ProcessingTime ?? '',
    };
}

function mapActivityLock(raw: any): ActivityLockItem {
    return {
        Key: raw.Key ?? '',
        HeldBy: raw.HeldBy ?? '',
        Acquired: parseActivityDate(raw.Acquired),
    };
}

export default datamonitorRepo;
